"use client"

import { shouldShowAptosLink } from "@/app/blockchains/aptos-link-visibility"
import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import * as amplitude from "@amplitude/analytics-browser"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import { useLingui } from "@lingui/react/macro"
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
  cn,
  useIsMobile,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { usePathname } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  AptosSelectItem,
  AptosTabItem,
} from "../../[blockchain]/(leaderboard)/aptos-links"
import { BlockchainFilterView, BlockchainSelectItem } from "./blockchain-filter"
import { BlockchainFiltersMoleculeReatom } from "./blockchain-filters-molecule.reatom"
import type { BlockchainFilter } from "./blockchain-filters-scope"

const MAX_TOGGLE_GROUP_BLOCKCHAINS = 5

interface Props {
  variant: "buttons" | "tabs" | "selector"
  className?: string
  onSelect?: (blockchain: Blockchain) => void
  blockchains: BlockchainFilter[]
  selectedBlockchain: BlockchainFilter
  tabContent?: React.ReactNode
  selectContent?: React.ReactNode
  showAptosLink?: boolean
}

function constructNewPath(currentPath: string, newBlockchain: string): string {
  const paths = currentPath.split("/").filter(Boolean)

  if (paths.length === 0) {
    // Root path
    return `/${newBlockchain.toLowerCase()}`
  }

  // Replace first segment (blockchain) with new blockchain
  paths[0] = newBlockchain.toLowerCase()
  return `/${paths.join("/")}`
}

export const BlockchainFilters = reatomComponent<{
  variant?: "buttons" | "tabs" | "selector"
  showAllChainsTab?: boolean
  className?: string
  tabContent?: React.ReactNode
  selectContent?: React.ReactNode
}>(
  ({
    variant = "buttons",
    showAllChainsTab = false,
    className,
    tabContent,
    selectContent,
  }) => {
    const { selectedBlockchain, availableBlockchains, setSelectedBlockchain } =
      useMolecule(BlockchainFiltersMoleculeReatom)
    const selectedBlockchainValue = selectedBlockchain()
    const allBlockchains = availableBlockchains()

    const logger = useMolecule(LoggerMolecule)

    const blockchainFilters = showAllChainsTab
      ? (["ALL", ...allBlockchains] as BlockchainFilter[])
      : (allBlockchains as BlockchainFilter[])

    const pathname = usePathname()

    const onSelect = useCallback(
      (value: string) => {
        if (value && value !== selectedBlockchainValue) {
          const newBlockchain = value as Blockchain

          // Track the selection
          amplitude.track("Blockchain selected", {
            blockchain: value,
          })

          setSelectedBlockchain(newBlockchain)
          // Update logger context with selected blockchain
          logger?.api.setContext({ blockchain: newBlockchain })

          // Fast client-side URL update without server navigation
          const newPath = constructNewPath(
            pathname,
            value === "ALL" ? "all" : newBlockchain,
          )

          window.history.pushState(null, "", newPath)

          // Dispatch custom event to notify components of URL change
          window.dispatchEvent(new PopStateEvent("popstate"))
        }
      },
      [selectedBlockchainValue, pathname, setSelectedBlockchain, logger],
    )

    return (
      <BlockchainFiltersView
        variant={variant}
        className={className}
        onSelect={onSelect}
        blockchains={blockchainFilters}
        selectedBlockchain={selectedBlockchainValue}
        tabContent={tabContent}
        selectContent={selectContent}
      />
    )
  },
  "BlockchainFilters",
)

export function BlockchainFiltersView({
  variant,
  className,
  onSelect,
  blockchains,
  selectedBlockchain,
  tabContent,
  selectContent,
  showAptosLink,
}: Props) {
  const { t } = useLingui()
  const { isEvmBlockchain: getIsEvmBlockchain } = useMolecule(
    BlockchainsMoleculeReatom,
  )
  const resolvedShowAptosLink =
    showAptosLink ??
    shouldShowAptosLink(selectedBlockchain, getIsEvmBlockchain())

  const isMobile = useIsMobile()
  const isTabs = variant === "tabs"
  const isSelector = variant === "selector"

  if (isSelector) {
    return (
      <BlockchainFiltersSelectView
        blockchains={blockchains}
        selectedBlockchain={selectedBlockchain}
        onChange={(value) => onSelect?.(value as unknown as Blockchain)}
        className={className}
        showAptosLink={resolvedShowAptosLink}
      />
    )
  }

  const toggleGroupBlockchains = useMemo(() => {
    if (isMobile && !isTabs) {
      return blockchains.slice(0, 1)
    }
    if (isTabs && isMobile) {
      return blockchains
    }
    if (isTabs && !isMobile) {
      return blockchains.slice(0, MAX_TOGGLE_GROUP_BLOCKCHAINS)
    }
    return blockchains.slice(0, MAX_TOGGLE_GROUP_BLOCKCHAINS)
  }, [blockchains, isMobile, isTabs])

  const selectBlockchains = useMemo(() => {
    if (isMobile && !isTabs) {
      return blockchains.slice(1)
    }
    if (isTabs && isMobile) {
      return []
    }
    if (isTabs && !isMobile) {
      return blockchains.slice(MAX_TOGGLE_GROUP_BLOCKCHAINS)
    }
    return blockchains.slice(MAX_TOGGLE_GROUP_BLOCKCHAINS)
  }, [blockchains, isMobile, isTabs])

  const toggleGroupRef = useRef<HTMLDivElement>(null)
  const selectTriggerRef = useRef<HTMLButtonElement>(null)
  const [isFirstRender, setIsFirstRender] = useState(true)

  const updateIndicatorPosition = useCallback(() => {
    if (!isTabs || isMobile || !toggleGroupRef.current) return

    const container = toggleGroupRef.current.parentElement
    if (!container) return

    let targetElement: HTMLElement | null = null

    const activeToggleItem = toggleGroupRef.current.querySelector(
      `[data-state="on"]`,
    ) as HTMLElement

    if (activeToggleItem) {
      targetElement = activeToggleItem
    } else if (selectBlockchains.includes(selectedBlockchain)) {
      if (selectTriggerRef.current) {
        targetElement = selectTriggerRef.current
      }
    }

    if (targetElement) {
      const containerRect = container.getBoundingClientRect()
      const targetRect = targetElement.getBoundingClientRect()

      const left = targetRect.left - containerRect.left
      const width = targetRect.width

      container.style.setProperty("--indicator-left", `${left}px`)
      container.style.setProperty("--indicator-width", `${width}px`)

      if (isFirstRender) {
        setTimeout(() => {
          setIsFirstRender(false)
        }, 100)
      }
    }
  }, [selectedBlockchain, isTabs, isMobile, selectBlockchains, isFirstRender])

  useEffect(() => {
    updateIndicatorPosition()
  }, [updateIndicatorPosition])

  useEffect(() => {
    if (!isTabs || isMobile) return

    const handleResize = () => {
      updateIndicatorPosition()
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [isTabs, isMobile, updateIndicatorPosition])

  return (
    <div
      className={cn(
        "flex w-full items-center",
        isTabs
          ? "relative justify-start border-b md:justify-center"
          : "gap-x-2",
        className,
      )}
    >
      <ToggleGroup
        ref={toggleGroupRef}
        type="single"
        value={selectedBlockchain}
        data-testid="blockchain-filters"
        onValueChange={onSelect}
        className={cn(
          isTabs
            ? "relative mb-[-1px] justify-start overflow-x-auto xl:justify-center"
            : "",
        )}
      >
        <div className={cn("flex items-center", isTabs ? "" : "gap-x-2")}>
          {toggleGroupBlockchains.map((blockchain: BlockchainFilter) => {
            return (
              <ToggleGroupItem
                key={blockchain}
                value={blockchain}
                disabled={false}
                className={cn(
                  "shrink-0 basis-auto transition-all",
                  isTabs
                    ? "h-auto rounded-none border-transparent border-b pb-4 text-foreground/100 data-[state=on]:border-foreground/100 data-[state=on]:border-b-foreground/100 data-[state=on]:bg-transparent data-[state=off]:opacity-60 data-[state=off]:hover:opacity-100 xl:border-b-transparent"
                    : "hover:bg-foreground/[0.06] hover:text-foreground/100",
                )}
              >
                <BlockchainFilterView
                  blockchain={blockchain}
                  isCompact={isMobile}
                  isSelected={selectedBlockchain === blockchain}
                />
              </ToggleGroupItem>
            )
          })}
          {isMobile && resolvedShowAptosLink && <AptosTabItem />}
          {!selectBlockchains.length && tabContent}
        </div>
      </ToggleGroup>

      {selectBlockchains.length > 0 && (
        <Select
          value={
            selectBlockchains.includes(selectedBlockchain)
              ? selectedBlockchain
              : ""
          }
          onValueChange={onSelect}
        >
          <SelectTrigger
            ref={selectTriggerRef}
            className={cn(
              "w-auto border-t-0 border-r-0 border-b-0 border-l-0 bg-transparent",
              isTabs
                ? "mb-[-1px] h-auto rounded-none bg-transparent pt-0 pb-4 text-foreground/60 outline-none focus-within:ring-0 hover:bg-transparent hover:text-foreground/100 focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                : "border-none",
              isTabs && selectBlockchains.includes(selectedBlockchain)
                ? "border-foreground/100 border-b-1 text-foreground/100 xl:border-b-0"
                : "",
            )}
          >
            <SelectValue placeholder={t`More`} />
          </SelectTrigger>
          <SelectContent>
            {selectBlockchains.map((blockchain) => (
              <BlockchainSelectItem key={blockchain} blockchain={blockchain} />
            ))}
            {resolvedShowAptosLink && <AptosSelectItem />}
            {selectContent}
          </SelectContent>
        </Select>
      )}
      {isTabs && (
        <div
          className={cn(
            "absolute bottom-[-1px] left-0 hidden h-[1px] bg-foreground/100 xl:block",
            !isFirstRender && "transition-all duration-300 ease-out",
          )}
          style={{
            width: "var(--indicator-width, 0px)",
            transform: "translateX(var(--indicator-left, 0px))",
          }}
        />
      )}
    </div>
  )
}

function BlockchainFiltersSelectView({
  blockchains,
  selectedBlockchain,
  onChange,
  className,
  showAptosLink,
}: {
  blockchains: BlockchainFilter[]
  selectedBlockchain: BlockchainFilter
  onChange: (value: string) => void
  className?: string
  showAptosLink: boolean
}) {
  const { t } = useLingui()

  return (
    <Select value={selectedBlockchain} onValueChange={onChange}>
      <SelectTrigger className={cn("w-auto", className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {blockchains.map((blockchain) => (
          <BlockchainSelectItem key={blockchain} blockchain={blockchain} />
        ))}
        {showAptosLink && <AptosSelectItem />}
      </SelectContent>
    </Select>
  )
}
